import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Edit,
  Trash2,
  Plus,
  X,
  Save,
  Loader2,
  AlertTriangle,
  ClipboardList,
  Filter,
  Download,
} from "lucide-react";
import { TransactionTracking } from "../../../../types/features/administration/Transaction";
import useToken from "../../../../hooks/auth/useToken";
import { transactionTrackingService } from "../../../../services/administration/transactionTrackingService";

const STATUS_OPTIONS = ["En attente", "Complétée", "Annulée", "Échouée", "Remboursée"];
const REASON_OPTIONS = [
  "Référence absente",
  "Montant différent",
  "Interac en vérification bancaire",
  "Interac non reçu",
  "Client n'a pas effectué le paiement",
  "Coordonnées bénéficiaire incorrectes",
  "Annulation client",
  "Autre",
];
const OPERATION_TYPES = ["Recharge", "Transfert", "Virement", "Retrait"];
const PAYMENT_METHODS = ["interact", "stripe", "moneris", "wallet", "autre"];
const OUI_NON = ["oui", "non"];

const EditModal: React.FC<{
  item: TransactionTracking | null;
  isOpen: boolean;
  isCreating: boolean;
  onClose: () => void;
  onSave: (data: Partial<TransactionTracking>, id?: number) => void;
  isLoading: boolean;
}> = ({ item, isOpen, isCreating, onClose, onSave, isLoading }) => {
  const [form, setForm] = useState<Partial<TransactionTracking>>({});

  useEffect(() => {
    if (isCreating) {
      setForm({
        status: "En attente",
        interac_received: "non",
        reference_copied: "non",
        transaction_date: new Date().toISOString().split("T")[0],
      });
    } else if (item) {
      setForm({
        ...item,
        transaction_date: item.transaction_date || "",
        transaction_time: item.transaction_time || "",
        resolution_date: item.resolution_date || "",
      });
    }
  }, [item, isCreating, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form, isCreating ? undefined : item?.id);
  };

  const inputClass =
    "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-800">
            {isCreating ? "Nouvelle ligne de suivi" : "Modifier la ligne de suivi"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Date</label>
              <input
                type="date"
                value={form.transaction_date || ""}
                onChange={(e) => setForm({ ...form, transaction_date: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              />
            </div>
            <div>
              <label className={labelClass}>Heure</label>
              <input
                type="time"
                value={form.transaction_time || ""}
                onChange={(e) => setForm({ ...form, transaction_time: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Nom du client</label>
              <input
                type="text"
                value={form.client_name || ""}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              />
            </div>
            <div>
              <label className={labelClass}>Email / Téléphone</label>
              <input
                type="text"
                value={form.client_contact || ""}
                onChange={(e) => setForm({ ...form, client_contact: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Type d'opération</label>
              <select
                value={form.operation_type || ""}
                onChange={(e) => setForm({ ...form, operation_type: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              >
                <option value="">--</option>
                {OPERATION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Montant prévu</label>
              <input
                type="text"
                value={form.expected_amount || ""}
                onChange={(e) => setForm({ ...form, expected_amount: e.target.value })}
                className={inputClass}
                placeholder="Ex: 100$"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className={labelClass}>Montant reçu</label>
              <input
                type="text"
                value={form.received_amount || ""}
                onChange={(e) => setForm({ ...form, received_amount: e.target.value })}
                className={inputClass}
                placeholder="Ex: 100$"
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Mode de paiement</label>
              <select
                value={form.payment_method || ""}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              >
                <option value="">--</option>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Interac reçu</label>
              <select
                value={form.interac_received || "non"}
                onChange={(e) => setForm({ ...form, interac_received: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              >
                {OUI_NON.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Référence copiée</label>
              <select
                value={form.reference_copied || "non"}
                onChange={(e) => setForm({ ...form, reference_copied: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              >
                {OUI_NON.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Statut</label>
              <select
                value={form.status || "En attente"}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Motif si attente/annulation</label>
              <select
                value={form.reason || ""}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              >
                <option value="">--</option>
                {REASON_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Agent</label>
              <input
                type="text"
                value={form.agent || ""}
                onChange={(e) => setForm({ ...form, agent: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              />
            </div>
            <div>
              <label className={labelClass}>Date de résolution</label>
              <input
                type="date"
                value={form.resolution_date || ""}
                onChange={(e) => setForm({ ...form, resolution_date: e.target.value })}
                className={inputClass}
                disabled={isLoading}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Action effectuée</label>
            <input
              type="text"
              value={form.action_taken || ""}
              onChange={(e) => setForm({ ...form, action_taken: e.target.value })}
              className={inputClass}
              disabled={isLoading}
            />
          </div>

          <div>
            <label className={labelClass}>Observations</label>
            <textarea
              value={form.observations || ""}
              onChange={(e) => setForm({ ...form, observations: e.target.value })}
              className={inputClass}
              rows={3}
              disabled={isLoading}
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isCreating ? "Création..." : "Modification..."}</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isCreating ? "Créer" : "Modifier"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteModal: React.FC<{
  item: TransactionTracking | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}> = ({ item, isOpen, onClose, onConfirm, isLoading }) => {
  if (!isOpen || !item) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center space-x-3 mb-6">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          <h2 className="text-xl font-bold text-gray-800">Confirmer la suppression</h2>
        </div>
        <p className="text-gray-600 mb-6">
          Supprimer la ligne de <strong>{item.client_name || "N/A"}</strong> du {item.transaction_date || "N/A"} ?
        </p>
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center justify-center space-x-2 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Suppression...</span>
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                <span>Supprimer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  if (!status) return <span className="text-gray-400">—</span>;
  const colors: Record<string, string> = {
    "En attente": "bg-yellow-100 text-yellow-700",
    Complétée: "bg-green-100 text-green-700",
    Annulée: "bg-red-100 text-red-700",
    Échouée: "bg-orange-100 text-orange-700",
    Remboursée: "bg-blue-100 text-blue-700",
  };
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${colors[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
};

const TransactionTrackingView: React.FC = () => {
  const { token } = useToken();
  const [items, setItems] = useState<TransactionTracking[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editingItem, setEditingItem] = useState<TransactionTracking | null>(null);
  const [deletingItem, setDeletingItem] = useState<TransactionTracking | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [stats, setStats] = useState<{ total: number; status_counts: Record<string, number> }>({
    total: 0,
    status_counts: {},
  });

  const itemsPerPage = 15;

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { items, total } = await transactionTrackingService.getAll(token, {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        status: statusFilter || undefined,
        search: search || undefined,
      });
      setItems(items);
      setTotal(total);
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, statusFilter, search]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const s = await transactionTrackingService.getStats(token);
      setStats(s);
    } catch (error) {
      console.error("Erreur stats:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSave = async (data: Partial<TransactionTracking>, id?: number) => {
    setIsEditLoading(true);
    try {
      if (isCreating) {
        await transactionTrackingService.create(token, data);
      } else if (id) {
        await transactionTrackingService.update(token, id, data);
      }
      setEditingItem(null);
      setIsCreating(false);
      fetchData();
      fetchStats();
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la sauvegarde");
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingItem?.id) return;
    setIsDeleteLoading(true);
    try {
      await transactionTrackingService.delete(token, deletingItem.id);
      setDeletingItem(null);
      fetchData();
      fetchStats();
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la suppression");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  const exportCSV = () => {
    const headers = [
      "Date",
      "Heure",
      "Nom du client",
      "Contact",
      "Type",
      "Montant prévu",
      "Montant reçu",
      "Mode paiement",
      "Interac reçu",
      "Référence copiée",
      "Statut",
      "Motif",
      "Agent",
      "Action",
      "Date résolution",
      "Observations",
    ];
    const rows = items.map((i) => [
      i.transaction_date,
      i.transaction_time,
      i.client_name,
      i.client_contact,
      i.operation_type,
      i.expected_amount,
      i.received_amount,
      i.payment_method,
      i.interac_received,
      i.reference_copied,
      i.status,
      i.reason,
      i.agent,
      i.action_taken,
      i.resolution_date,
      i.observations,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "suivi_transactions.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
        </div>
        {(Object.entries(stats.status_counts) as [string, number][]).slice(0, 3).map(([status, count]) => (
          <div key={status} className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-sm text-gray-500">{status}</p>
            <p className="text-2xl font-bold text-blue-600">{count}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher client, agent, type..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="">Tous les statuts</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            onClick={exportCSV}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={() => {
              setIsCreating(true);
              setEditingItem({});
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            Ajouter
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : items.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Client</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Prévu</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Reçu</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Mode</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Interac</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Réf.</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Statut</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Agent</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-gray-800">{item.transaction_date || "—"}</p>
                      <p className="text-xs text-gray-400">{item.transaction_time || ""}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800 truncate max-w-[150px]">{item.client_name || "—"}</p>
                      <p className="text-xs text-gray-400 truncate max-w-[150px]">{item.client_contact || ""}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.operation_type || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{item.expected_amount || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{item.received_amount || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{item.payment_method || "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${item.interac_received === "oui" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                      >
                        {item.interac_received || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${item.reference_copied === "oui" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                      >
                        {item.reference_copied || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.agent || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => {
                            setIsCreating(false);
                            setEditingItem(item);
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingItem(item)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune ligne de suivi trouvée</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Page {currentPage} sur {totalPages} ({total} lignes)
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-100"
              >
                Précédent
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-100"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <EditModal
        item={editingItem}
        isOpen={!!editingItem}
        isCreating={isCreating}
        onClose={() => {
          setEditingItem(null);
          setIsCreating(false);
        }}
        onSave={handleSave}
        isLoading={isEditLoading}
      />
      <DeleteModal
        item={deletingItem}
        isOpen={!!deletingItem}
        onClose={() => setDeletingItem(null)}
        onConfirm={handleDelete}
        isLoading={isDeleteLoading}
      />
    </div>
  );
};

export default TransactionTrackingView;
